import { comparisonPageIndexable, comparisonSearchIndexable, parseComparisonPath } from './catalogue-seo.js';
import { guides, guidePath } from './guides.js';
import { brandMetaDescription, mainAccords, recordedLongevity, type BrandMetaSummary } from './brand-landing.js';
import { familyHubs, noteHubs } from './discovery.js';
import { verifiedFragranceNotes } from './verified-fragrance-notes.js';
type Brand = { name: string; slug: string };
type Fragrance = { name: string; concentration: string; isOriginal: boolean; originals?: unknown[]; alternatives?: unknown[]; brand: { name: string } | null; scentProfile?: unknown };
export type PageMetadata = { title: string; description: string; canonical: string; noindex: boolean };

const concentrationAliases: Record<string, string[]> = {
  'eau de parfum': ['edp'],
  'eau de toilette': ['edt'],
  'eau de cologne': ['edc'],
};

function normaliseIdentityPart(value: string): string {
  return value.trim().toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').replace(/\s+/g, ' ').trim();
}

function startsWithPhrase(value: string, phrase: string): boolean {
  return value === phrase || value.startsWith(phrase + ' ');
}

function endsWithPhrase(value: string, phrase: string): boolean {
  return value === phrase || value.endsWith(' ' + phrase);
}

export function fragranceIdentity(fragrance: Pick<Fragrance, 'name' | 'concentration' | 'brand'>): string {
  const name = fragrance.name.trim();
  const brand = fragrance.brand?.name.trim() ?? '';
  const concentration = fragrance.concentration.trim();
  const normalisedName = normaliseIdentityPart(name);
  const normalisedBrand = normaliseIdentityPart(brand);
  const normalisedConcentration = normaliseIdentityPart(concentration);
  const aliases = concentrationAliases[normalisedConcentration] ?? [];
  const nameHasBrand = Boolean(normalisedBrand && startsWithPhrase(normalisedName, normalisedBrand));
  const nameHasConcentration = Boolean(normalisedConcentration && [normalisedConcentration, ...aliases].some(alias => endsWithPhrase(normalisedName, alias)));
  return [nameHasBrand ? '' : brand, name, nameHasConcentration ? '' : concentration].filter(Boolean).join(' ');
}

/** Brand and name only: enough to tell two fragrances apart in a title that names both. */
export function fragranceLabel(fragrance: Pick<Fragrance, 'name' | 'brand'>): string {
  const name = fragrance.name.trim();
  const brand = fragrance.brand?.name.trim() ?? '';
  if (!brand) return name;
  return startsWithPhrase(normaliseIdentityPart(name), normaliseIdentityPart(brand)) ? name : brand + ' ' + name;
}

export function canonicalPath(href: string): string {
  const { pathname, searchParams } = new URL(href, 'https://identifrag.example');
  const brandPage = /^\/brands\/[^/]+$/.test(pathname);
  const hubPage = /^\/(notes|families)\/[^/]+$/.test(pathname);
  if (pathname !== '/' && !brandPage && !hubPage) return pathname;
  if (['q', 'brand', 'gender', 'concentration', 'accord'].some(key => searchParams.get(key))) return pathname;
  const page = Math.max(1, Math.min(10000, Number.parseInt(searchParams.get('page') ?? '1', 10) || 1));
  return page > 1 ? pathname + '?page=' + page : pathname;
}

export type ComparisonMeta = {
  original: Pick<Fragrance, 'name' | 'brand'> & { scentProfile?: unknown };
  alternative: Pick<Fragrance, 'name' | 'brand'> & { scentProfile?: unknown };
  closeDupe?: boolean;
  /** A published relationship, direct or via a shared original; false for a pair a visitor chose. */
  editorial?: boolean;
};
export function pageMetadata(href: string, data: { brand?: Brand; brandSummary?: BrandMetaSummary; detail?: Fragrance; comparison?: ComparisonMeta; error?: string } = {}): PageMetadata {
  const { pathname } = new URL(href, 'https://identifrag.example');
  const canonical = canonicalPath(href);
  const page = new URL(canonical, 'https://identifrag.example').searchParams.get('page');
  const suffix = (page ? ' – Page ' + page : '') + ' | Identifrag';
  let title = 'Compare Fragrances by Scent Profile' + suffix;
  let description = 'Explore fragrances by scent notes, accords and estimated wear. Compare specific releases and find buying links for your country.';
  if (data.error) {
    title = data.error + ' | Identifrag';
    description = 'This page is unavailable. Browse the fragrance collection to compare scent profiles.';
  } else if (pathname === '/') {
    title = 'Find Fragrances by Scent Profile' + suffix;
    description = 'Search fragrances by name and explore scent profiles, recorded accords and maker-listed notes where available.';
  } else if (pathname.startsWith('/admin')) {
    title = 'Editorial workspace | Identifrag';
    description = 'Private editorial workspace for Identifrag.';
  } else if (data.comparison) {
    const alternative = fragranceLabel(data.comparison.alternative);
    const original = fragranceLabel(data.comparison.original);
    const includesChanel = [data.comparison.original, data.comparison.alternative]
      .some((fragrance) => fragrance.brand?.name.toLowerCase() === 'chanel');
    title = alternative + ' vs ' + original + ' | Identifrag';
    description = `Compare ${alternative} with ${original}: scent notes and accords, estimated longevity and projection${includesChanel ? '.' : ', and retailer buying links.'}`;
  } else if (data.detail) {
    const f = data.detail;
    const name = fragranceIdentity(f);
    const makerNotes = verifiedFragranceNotes[pathname.slice('/fragrances/'.length)];
    const accords = mainAccords(f.scentProfile, 2).map(accord => accord.name);
    const longevity = recordedLongevity(f.scentProfile);
    title = name + (makerNotes ? ': Notes & Scent Profile' : accords.length ? ': Scent Profile & Accords' : ': Fragrance Profile') + ' | Identifrag';
    description = makerNotes
      ? `Explore manufacturer-listed notes for ${name}, alongside Identifrag’s editorial accord profile and estimated wear.`
      : accords.length
        ? `Explore ${name}: recorded ${accords.join(' and ')} ${accords.length === 1 ? 'accord' : 'accords'}${longevity ? ' and an editorial wear estimate' : ''}.`
        : `Explore ${name} in the Identifrag catalogue, with its fragrance description and release details.`;
  } else if (data.brand) {
    title = data.brand.name + ' Fragrances' + suffix;
    // Brand descriptions quote the catalogue's own counts once the landing data is available.
    description = data.brandSummary?.fragrances
      ? brandMetaDescription(data.brand.name, data.brandSummary)
      : `Explore ${data.brand.name} fragrances in the Identifrag catalogue, with scent profiles and concentrations where recorded.`;
  } else if (pathname === '/brands') {
    title = 'Browse Perfume Brands | Identifrag';
    description = 'Browse fragrance houses in the Identifrag catalogue and explore their recorded scent profiles.';
  } else if (pathname === '/notes') {
    title = 'Perfume Notes & Accords | Identifrag';
    description = 'Explore fragrances through recorded notes and accords, from oud and vanilla to fig and leather.';
  } else if (pathname === '/finder') {
    title = 'Find a Fragrance by Scent | Identifrag';
    description = 'Start with a scent profile or combine fragrance families and notes you enjoy to discover matching perfumes.';
  } else if (pathname === '/compare') {
    title = 'Compare Two Fragrances | Identifrag';
    description = 'Choose two fragrances to view their recorded notes, accords and wear estimates side by side.';
  } else if (pathname === '/families') {
    title = 'Scent Families | Identifrag';
    description = 'Explore gourmand, fresh, citrus, woody and amber fragrance families.';
  } else if (pathname.startsWith('/notes/') && noteHubs.includes(pathname.slice(7) as typeof noteHubs[number])) {
    const note = pathname.slice(7);
    title = note[0]!.toUpperCase() + note.slice(1) + ' Perfumes: Notes & Accords' + suffix;
    description = `Browse perfumes with a recorded ${note} note or accord in the Identifrag catalogue, ordered by prominence.`;
  } else if (pathname.startsWith('/families/') && pathname.slice(10) in familyHubs) {
    const family = pathname.slice(10);
    title = family[0]!.toUpperCase() + family.slice(1) + ' Perfumes' + suffix;
    description = `Browse ${family} fragrances across different houses, grouped by their recorded notes and accords.`;
  } else if (pathname === '/guides') {
    title = 'Fragrance Guides | Identifrag';
    description = 'Explore practical fragrance guides on notes, concentration labels, seasonal scents and testing perfume on skin.';
  } else if (guides.some(guide => guidePath(guide.slug) === pathname)) {
    const guide = guides.find(guide => guidePath(guide.slug) === pathname)!;
    title = guide.title + ' | Identifrag';
    description = guide.description;
  } else if (pathname === '/about') {
    title = 'About Identifrag | Fragrance Profiles & Methods';
    description = 'Learn how Identifrag records scent profiles, explains editorial wear estimates and presents fragrance information.';
  } else if (pathname === '/privacy') {
    title = 'Privacy & Cookies | Identifrag';
    description = 'How Identifrag handles browsing information, contact enquiries, browser storage and affiliate links, and how to exercise your privacy rights.';
  } else if (pathname.startsWith('/profiles/')) {
    title = 'Member profile | Identifrag';
    description = 'View this member’s fragrance reviews on Identifrag.';
  } else if (['/sign-in','/register','/account','/profile','/forgot-password','/reset-password','/verify-email'].includes(pathname)) {
    title = ({'/sign-in':'Sign in','/register':'Create an account','/account':'Your account','/profile':'Your profile','/forgot-password':'Reset your password','/reset-password':'Choose a new password','/verify-email':'Verify your email'}[pathname] ?? 'Your account') + ' | Identifrag';
    description = 'Sign in and manage your Identifrag account, security and personal information.';
  } else if (pathname === '/contact') {
    title = 'Contact | Identifrag';
    description = 'Get in touch with Identifrag about the fragrance catalogue, editorial questions or business enquiries.';
  } else if (pathname !== '/' && !parseComparisonPath(pathname)) {
    title = 'Page not found | Identifrag';
    description = 'This page is unavailable. Browse the Identifrag fragrance collection instead.';
  }
  return { title, description, canonical, noindex: pathname === '/compare' || (pathname.startsWith('/compare/') && !(data.comparison && !data.error && comparisonSearchIndexable(data.comparison))) || pathname.startsWith('/profiles/') || pathname.startsWith('/admin') || ['/sign-in','/register','/account','/profile','/forgot-password','/reset-password','/verify-email'].includes(pathname) || Boolean(data.detail && !comparisonPageIndexable(data.detail)) };
}
